import { ArrowDownward, CallMade, CallReceived } from '@mui/icons-material';
import { alpha, Box, IconButton, keyframes, Tooltip, Typography } from '@mui/material';

const focusAnimation = keyframes`
  0% { background-color: transparent; }
  25% { background-color: ${alpha('#95A065', 0.3)}; }
  75% { background-color: ${alpha('#95A065', 0.2)}; }
  100% { background-color: transparent; }
`;

interface ToolCallDisplayProps {
  type: 'call' | 'result';
  toolName: string;
  toolCallId: string;
  data: unknown;
  isFocused?: boolean;
  onJumpToResult?: (toolCallId: string) => void;
}

export function ToolCallDisplay({
  type,
  toolName,
  toolCallId,
  data,
  isFocused = false,
  onJumpToResult,
}: ToolCallDisplayProps) {
  const icon = type === 'call' ? <CallMade fontSize="small" /> : <CallReceived fontSize="small" />;
  const label = type === 'call' ? 'Tool Call' : 'Tool Result';
  const color = type === 'call' ? 'primary.main' : 'success.main';

  const formatData = (d: unknown): string => {
    if (typeof d === 'string') return d;
    try {
      return JSON.stringify(d, null, 2);
    } catch {
      return String(d);
    }
  };

  return (
    <Box
      id={`tool-${type}-${toolCallId}`}
      sx={{ my: 1, animation: isFocused ? `${focusAnimation} 2s ease-in-out` : 'none', borderRadius: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 'medium', flex: 1 }}>
          {label}: {toolName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
          {toolCallId.slice(0, 12)}...
        </Typography>
        {type === 'call' && onJumpToResult && (
          <Tooltip title="Jump to result">
            <IconButton size="small" onClick={() => onJumpToResult(toolCallId)} sx={{ p: 0.25 }}>
              <ArrowDownward sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          mt: 0.5,
          p: 1,
          bgcolor: 'grey.50',
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '11px',
          maxHeight: '150px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {formatData(data)}
      </Box>
    </Box>
  );
}
